import { UserIncentive } from '@tovala/browser-apis-combinedapi'
import { formatCentsToDollars, hasCentsLeftOver } from './currency'
import { UserIncentiveWithFreeDessert } from 'types/internal'

export function makeLegacyFreeDessertIncentive(): UserIncentiveWithFreeDessert {
  return {
    offerID: 'free-dessert',
    internalDescription: '',
    titleOverride: '',
    mealDiscountType: 'percent',
    mealDiscountValue: 0,
    shippingDiscountType: 'percent',
    shippingDiscountValue: 0,
    totalOrdersCount: 0,
    redeemedOrderCount: 0,
    applicability: 'freeDessertLegacy',
    useByDate: null,
  }
}

export function formatMealDiscountValue(value: number) {
  return formatCentsToDollars(value, {
    digits: hasCentsLeftOver(value) ? 2 : 0,
  })
}

export function hasFreeShipping(
  incentive: UserIncentive | UserIncentiveWithFreeDessert,
): boolean {
  return (
    incentive.shippingDiscountType === 'percent' &&
    incentive.shippingDiscountValue === 100
  )
}

export function hasMealDiscount(
  incentive: UserIncentive | UserIncentiveWithFreeDessert,
): boolean {
  return incentive.mealDiscountValue > 0
}

export function getMealDiscountText(
  incentive: UserIncentive | UserIncentiveWithFreeDessert,
): string {
  return incentive.mealDiscountType === 'percent'
    ? `${incentive.mealDiscountValue}% off`
    : `${formatMealDiscountValue(incentive.mealDiscountValue)} off`
}

export function getRemainingOrdersCount(
  incentive: UserIncentive | UserIncentiveWithFreeDessert,
): number | null {
  if (incentive.totalOrdersCount === null) {
    return null
  }

  return incentive.totalOrdersCount - incentive.redeemedOrderCount
}

export function getAppliesToOrdersText(
  incentive: UserIncentive | UserIncentiveWithFreeDessert,
): string {
  const remainingOrdersCount = getRemainingOrdersCount(incentive)

  if (remainingOrdersCount === null) {
    return 'every order'
  }

  return remainingOrdersCount === 1
    ? 'your next order'
    : `your next ${remainingOrdersCount} orders`
}

export function getAutoAppliesToDeliveriesText(
  incentive: UserIncentive | UserIncentiveWithFreeDessert,
): string {
  const remainingOrdersCount = getRemainingOrdersCount(incentive)

  if (remainingOrdersCount === null) {
    return 'Auto applies to every delivery'
  }

  return remainingOrdersCount <= 1
    ? 'Auto applies to your next delivery'
    : `Auto applies to your next ${remainingOrdersCount} deliveries`
}

export function getAutoApplyIncentiveCardContent(
  incentive: UserIncentive | UserIncentiveWithFreeDessert,
): { title: string; subtitle: string } {
  const isUnlimited = getRemainingOrdersCount(incentive) === null

  let title = ''
  let subtitle = getAutoAppliesToDeliveriesText(incentive)

  if (hasFreeShipping(incentive) && hasMealDiscount(incentive)) {
    title = `Free shipping and ${getMealDiscountText(incentive)} your order`
  } else if (hasFreeShipping(incentive)) {
    title = isUnlimited ? 'Free shipping for life' : 'Free shipping next order'
  } else if (hasMealDiscount(incentive)) {
    title = `${getMealDiscountText(incentive)} your order`
  } else if (isLegacyFreeDessertIncentive(incentive)) {
    title = 'Free dessert for life'
    subtitle = 'Auto applies to your next delivery'
  }

  return {
    title: incentive.titleOverride || title,
    subtitle,
  }
}

export function sortIncentivesByUseByDate(
  incentives: UserIncentiveWithFreeDessert[],
) {
  return [...incentives].sort((a, b) => {
    if (a.useByDate === null && b.useByDate === null) {
      return 0 // Maintain relative order of nulls
    }
    if (a.useByDate === null) {
      return 1 // 'a' (null) comes after 'b' (date)
    }
    if (b.useByDate === null) {
      return -1 // 'a' (date) comes before 'b' (null)
    }
    return new Date(a.useByDate).getTime() - new Date(b.useByDate).getTime()
  })
}

export function getActivePromoCount({
  activeIncentives,
  isFreeDessertCustomer,
}: {
  activeIncentives: UserIncentive[]
  isFreeDessertCustomer: boolean
}) {
  return activeIncentives.length + (isFreeDessertCustomer ? 1 : 0)
}

export function getIncentivesToDisplay({
  activeIncentives,
  isFreeDessertCustomer,
}: {
  activeIncentives: UserIncentive[]
  isFreeDessertCustomer: boolean
}) {
  const incentives: UserIncentiveWithFreeDessert[] = [
    ...activeIncentives,
    ...(isFreeDessertCustomer ? [makeLegacyFreeDessertIncentive()] : []),
  ]

  const incentivesToDisplay = sortIncentivesByUseByDate(
    incentives.filter(
      (incentive) =>
        incentive.applicability === 'nextOrders' ||
        incentive.applicability === 'freeDessertLegacy',
    ),
  )

  return incentivesToDisplay
}

export function isLegacyFreeDessertIncentive(
  incentive: UserIncentive | UserIncentiveWithFreeDessert,
) {
  return incentive.applicability === 'freeDessertLegacy'
}
